import { ChangeEvent, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, SearchInput } from '@ds';
import { MdOutlineAdd } from 'react-icons/md';
import { AdminPageHeader, ConditionalRender, ConfirmationModal, DataTable, DeleteModal } from '@ui';
import {
  getRenderHubTableCell,
  HUB_TABLE_COLUMNS,
  HUBS_LIST_FILTERS_BASE,
  HubsFilters,
} from '@lp/features/admin/tables/HubsListTable';
import { AuthContext } from '@lp/core/auth';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { useHubsListQuery, useUpdateHubMutation } from '@/queries/hub';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { Hub } from '@/api/apis/facilities';
import { useAllRegionsListQuery } from '@/queries/regions';
import { useWrappedRemoveHubMutation } from '@/queries/orchestrator';
import { AdminPermissions } from '@/permissions';

export const HubsList = () => {
  const [hubToChange, setHubToChange] = useState<Hub | undefined>();
  const [confirmationModalTitle, setConfirmationModalTitle] = useState<string>('');
  const [confirmationModalDesc, setConfirmationModalDesc] = useState<string>('');
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const { mutateAsync } = useUpdateHubMutation();
  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.NopHubCreate);
  const hasRemovePermissions = checkPermission(AdminPermissions.NopHubRemove);

  const navigate = useNavigate();
  const {
    data,
    isLoading,
    isFetching,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: HUBS_LIST_FILTERS_BASE,
    query: useHubsListQuery,
  });

  const { data: regionsList } = useAllRegionsListQuery();

  const { isOpen, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    query: useWrappedRemoveHubMutation,
    successMessage: 'Hub deleted successfully.',
  });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const onIsSuperhubSwitchChanged = useCallback((hub: Hub) => {
    setConfirmationModalTitle('Change Super Hub status?');
    setConfirmationModalDesc('Are you sure you want to change Super Hub status?');
    setHubToChange(hub);
    setConfirmationModalOpen(true);
  }, []);

  const onChangeSuperhub = async () => {
    if (hubToChange) {
      await mutateAsync({
        id: hubToChange?.id,
        isSuperHub: !hubToChange?.isSuperHub,
      });
    }
  };

  const onConfirmationModalClose = useCallback(() => {
    setHubToChange(undefined);
    setConfirmationModalOpen(false);
  }, []);

  const handleCreate = () => {
    navigate('/admin/hubs/locations/create');
  };

  const handleEdit = useCallback(
    (hubId: string) => {
      navigate(`/admin/hubs/locations/${hubId}`);
    },
    [navigate],
  );

  const { resolveSubdivisionName } = useCountriesData();
  const renderHubTableCell = useMemo(() => {
    return getRenderHubTableCell({
      regionsList,
      onEdit: handleEdit,
      onDelete: onModalOpen,
      onSuperhubChange: onIsSuperhubSwitchChanged,
      hasCreatePermissions,
      hasRemovePermissions,
      resolveSubdivisionName,
    });
  }, [
    regionsList,
    handleEdit,
    onModalOpen,
    onIsSuperhubSwitchChanged,
    hasCreatePermissions,
    hasRemovePermissions,
    resolveSubdivisionName,
  ]);

  const HUB_TABLE_COLUMNS_FILTERED = useMemo(() => {
    return hasCreatePermissions ? HUB_TABLE_COLUMNS : HUB_TABLE_COLUMNS.filter((item) => item.key !== 'actions');
  }, [hasCreatePermissions]);

  return (
    <>
      <div className="flex flex-col place-items-stretch gap-9">
        <AdminPageHeader title="NOP Hubs">
          <div className="max-w-[480px] grow">
            <SearchInput placeholder="Search" value={searchText} onChange={onSearchChange} inputVariant="secondary" />
          </div>
          <ConditionalRender show={hasCreatePermissions}>
            <div className="shrink-0">
              <Button size="md" variant="solid" endContent={<MdOutlineAdd size={18} />} onPress={handleCreate}>
                Create
              </Button>
            </div>
          </ConditionalRender>
        </AdminPageHeader>
        <div className="flex flex-col gap-4">
          <HubsFilters
            filters={filters}
            hasFilters={hasFilters}
            updateFilters={updateFilters}
            clearFilters={clearFilters}
          />
          <DataTable
            label="NOP Hubs"
            columns={HUB_TABLE_COLUMNS_FILTERED}
            renderCell={renderHubTableCell}
            data={data?.items || []}
            isLoading={isLoading}
            isFetching={isFetching}
            totalItems={data?.totalItems || 0}
            totalPages={data?.totalPages || 1}
            page={page}
            itemsPerPage={itemsPerPage}
            sortBy={sortBy}
            onPageChange={setPage}
            onItemsPerPageChange={setItemsPerPage}
            onSortByChange={setSortBy}
          />
        </div>
      </div>
      <ConfirmationModal
        title={confirmationModalTitle}
        description={confirmationModalDesc}
        isOpen={confirmationModalOpen}
        onChangeActive={onChangeSuperhub}
        onClose={onConfirmationModalClose}
      />
      <DeleteModal
        title="Delete NOP Hub?"
        description="Are you sure you want to delete the NOP Hub?"
        isOpen={isOpen}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
